import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../config/keys';
import { db, auth } from './config';
import { doc, updateDoc, increment, collection, addDoc, serverTimestamp, query, getDocs, where } from 'firebase/firestore';

// Initialize Stripe
let stripePromise = null;
try {
  stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
} catch (error) {
  console.warn('Stripe initialization failed:', error);
}

// Simplified payment function that handles credits directly on frontend
export const createSimpleStripePayment = async (amount, currency, userId, credits, packageId = null, email = null) => {
  try {
    console.log(`Creating simple Stripe payment for ${credits} credits at ${amount} ${currency}`);

    // Validate inputs
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    console.log('User authenticated:', currentUser.uid);

    // Get domain for success/cancel URLs
    const domain = window.location.origin;

    // Create Stripe checkout session directly
    const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/createStripeCheckoutApi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        currency,
        userId,
        credits,
        packageId,
        packageId,
        email: email || (currentUser ? currentUser.email : null),
        successUrl: `${domain}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${domain}/payment-cancel?session_id={CHECKOUT_SESSION_ID}`
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('Stripe session created:', data);

    // Store transaction in Firestore for tracking
    try {
      await addDoc(collection(db, 'transactions'), {
        userId,
        amount,
        currency,
        credits,
        packageId,
        paymentId: data.sessionId,
        status: 'pending',
        paymentProvider: 'stripe',
        createdAt: serverTimestamp(),
        visible: true
      });
      console.log('Transaction record created');
    } catch (dbError) {
      console.error('Error creating transaction record:', dbError);
      // Continue with payment even if transaction record fails
    }

    return data;
  } catch (error) {
    console.error('Error creating simple Stripe payment:', error);
    throw error;
  }
};

// Function to add credits directly to user account
export const addCreditsToUser = async (userId, credits, transactionId = null) => {
  try {
    console.log(`Adding ${credits} credits to user ${userId}`);

    // Update user credits
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      credits: increment(credits),
      updatedAt: serverTimestamp()
    });

    // Update transaction status if provided
    if (transactionId) {
      const transactionRef = doc(db, 'transactions', transactionId);
      await updateDoc(transactionRef, {
        status: 'completed',
        updatedAt: serverTimestamp()
      });
    }

    console.log(`Successfully added ${credits} credits to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error adding credits to user:', error);
    throw error;
  }
};

// Function to check payment status and add credits
export const checkPaymentAndAddCredits = async (sessionId, userId) => {
  // Handle both user.id and user.uid formats
  const actualUserId = userId || (auth.currentUser ? auth.currentUser.uid : null);

  if (!actualUserId) {
    throw new Error('User ID is required for payment verification');
  }
  try {
    console.log('Checking payment status for session:', sessionId);

    // Find the transaction by paymentId (session ID)
    const transactionsRef = collection(db, 'transactions');
    const q = query(transactionsRef, where('paymentId', '==', sessionId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const transactionDoc = querySnapshot.docs[0];
      const transactionData = transactionDoc.data();

      console.log('Found transaction:', transactionData);

      if (transactionData.status === 'pending') {
        // Add credits to user
        await addCreditsToUser(actualUserId, transactionData.credits, transactionDoc.id);

        // Update transaction status
        await updateDoc(transactionDoc.ref, {
          status: 'completed',
          updatedAt: serverTimestamp()
        });

        console.log('Payment completed and credits added');
        return {
          success: true,
          credits: transactionData.credits,
          message: `Successfully added ${transactionData.credits} credits to your account`
        };
      } else if (transactionData.status === 'completed') {
        console.log('Transaction already completed');
        return {
          success: true,
          credits: transactionData.credits,
          message: `Payment already processed! ${transactionData.credits} credits have been added to your account`
        };
      }
    } else {
      console.log('No transaction found for session:', sessionId);
      return {
        success: false,
        message: 'Transaction not found. Please contact support.'
      };
    }
  } catch (error) {
    console.error('Error checking payment and adding credits:', error);
    throw error;
  }
};

// Function to manually add credits (for testing or manual credit addition)
export const manuallyAddCredits = async (userId, credits, reason = 'Manual credit addition') => {
  try {
    console.log(`Manually adding ${credits} credits to user ${userId}`);

    // Add credits to user
    await addCreditsToUser(userId, credits);

    // Create a manual transaction record
    await addDoc(collection(db, 'transactions'), {
      userId,
      amount: 0,
      currency: 'USD',
      credits,
      packageId: 'manual',
      paymentId: `manual_${Date.now()}`,
      status: 'completed',
      paymentProvider: 'manual',
      reason,
      createdAt: serverTimestamp(),
      visible: true
    });

    console.log(`Successfully manually added ${credits} credits to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error manually adding credits:', error);
    throw error;
  }
}; 